/**
 * components/crypto-receive-card — ПЕРЕИСПОЛЬЗУЕМАЯ карта приёма крипты (QR + адрес +
 * копирование). Тонкий примитив-«ствол»: монтируется N× на любой surface (сейчас —
 * экран «Поддержать»; завтра — строка товара будущего магазина). Сам не знает, где стоит.
 *
 * ЗАЧЕМ / WHY THIS EXISTS (owner-directive 2026-06-26 «модульность/примитивы»): карта
 * приёма — отдельный кубик, а не вшита в экран → переиспользуема для магазина (зерно
 * Phase 2). Данные (адрес/сеть) приходят из ЕДИНОГО конфига `lib/donate-targets`; UI-
 * подписи (кнопка «Скопировать»/«Готово») — из каталога i18n через caller (не хардкод).
 *
 * Состав (вертикаль): [ заголовок сети + под-строка ] · [ QR на белой подложке ] ·
 *   [ ПОЛНЫЙ адрес моноширинно, выделяемый — для доверия крипто-аудитории ] ·
 *   [ pill-кнопка «♥ Скопировать адрес» → морф в «✓ Готово» ]. QR — `lib/qr-svg`
 *   (детерминированный inline-SVG, без CDN). Копирование робастно к LAN-http (где
 *   navigator.clipboard недоступен): фолбэк на textarea+execCommand.
 *
 * SCOPE: рендер карты + копирование + морф кнопки. NOT IN SCOPE: layout сетки карт
 *   (экран), выбор адресов (lib/donate-targets), тосты (caller через onCopy).
 *
 * Cross-references: lib/donate-targets; lib/qr-svg; components/pill-button (морф-кнопка);
 *   responsive-ui §5.0 (layout-plan — в crypto-receive-card.css).
 */

import './crypto-receive-card.css';

import type { DonateTarget } from '../lib/donate-targets.js';
import { qrSvg } from '../lib/qr-svg.js';
import { createPillButton } from './pill-button.js';

/** Filled-heart глиф ведущей иконки кнопки копирования (Tabler heart-filled). */
const HEART_GLYPH =
  `<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">` +
  `<path d="M6.979 3.074a6 6 0 0 1 4.988 1.425l.037 .033l.034 -.03a6 6 0 0 1 4.733 -1.44l.246 ` +
  `.036a6 6 0 0 1 3.364 10.008l-.18 .185l-.048 .041l-7.45 7.379a1 1 0 0 1 -1.313 .082l-.094 ` +
  `-.082l-7.493 -7.422a6 6 0 0 1 3.476 -10.46z"/></svg>`;

/** Check глиф «скопировано» (Tabler check, outline). */
const CHECK_GLYPH =
  `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" ` +
  `stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">` +
  `<path d="M5 12l5 5l9 -9"/></svg>`;

export interface CryptoReceiveCardOptions {
  /** Цель приёма (сеть + адрес) из конфига. */
  readonly target: DonateTarget;
  /** Локализованный префикс строки сети, напр. «Сеть:» / «Network:». Перед target.network. */
  readonly networkLabel?: string;
  /** Подпись кнопки копирования (локализованная), напр. «Скопировать адрес». */
  readonly copyLabel: string;
  /** Подпись кнопки после копирования (локализованная), напр. «Готово». */
  readonly copiedLabel: string;
  /** Доступное имя кнопки (локализованное); по умолчанию = copyLabel + сеть. */
  readonly copyAriaLabel?: string;
  /** Колбэк после попытки копирования (для тоста). ok=false → не удалось. */
  readonly onCopy?: (ok: boolean, target: DonateTarget) => void;
}

export interface CryptoReceiveCardHandle {
  readonly element: HTMLElement;
  destroy(): void;
}

/** Робастное копирование текста: clipboard API → фолбэк textarea+execCommand (LAN-http). */
async function copyText(text: string): Promise<boolean> {
  try {
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* clipboard API недоступен/запрещён — пробуем legacy ниже */
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.top = '-9999px';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    ta.remove();
    return ok;
  } catch {
    return false;
  }
}

/** Смонтировать карту приёма в `host`. Возвращает handle с `element` + `destroy()`. */
export function mountCryptoReceiveCard(
  host: HTMLElement,
  opts: CryptoReceiveCardOptions,
): CryptoReceiveCardHandle {
  const { target } = opts;

  const card = document.createElement('article');
  card.className = 'crypto-card';
  card.dataset.target = target.id;

  // Заголовок сети + под-строка (бренд/тикеры — не локализуются, lib/donate-targets).
  const head = document.createElement('header');
  head.className = 'crypto-card-head';
  const titleEl = document.createElement('span');
  titleEl.className = 'crypto-card-title';
  titleEl.textContent = target.title;
  const netEl = document.createElement('span');
  netEl.className = 'crypto-card-net';
  // «Сеть: TRON / TRC20» — локализованный префикс + бренд-имя сети (owner-format 2026-06-26).
  netEl.textContent = opts.networkLabel ? `${opts.networkLabel} ${target.network}` : target.network;
  head.append(titleEl, netEl);

  // QR на белой подложке (тёмные модули на светлом — требование сканируемости, css).
  const qr = document.createElement('div');
  qr.className = 'crypto-qr';
  qr.innerHTML = qrSvg(target.address);

  // ПОЛНЫЙ адрес моноширинно, выделяемый (доверие: крипто-аудитория сверяет адрес).
  const addr = document.createElement('code');
  addr.className = 'crypto-addr';
  addr.textContent = target.address;

  // Кнопка «♥ Скопировать адрес» → морф в «✓ Готово» (примитив pill-button).
  const pill = createPillButton({
    icon: HEART_GLYPH,
    label: opts.copyLabel,
    ariaLabel: opts.copyAriaLabel ?? `${opts.copyLabel} — ${target.title} (${target.network})`,
    className: 'crypto-copy',
  });
  const btn = pill.element;
  const iconSlot = btn.querySelector<HTMLElement>('.pill-button-icon');
  const labelSlot = btn.querySelector<HTMLElement>('.pill-button-label');

  let morphTimer: number | null = null;
  const resetMorph = (): void => {
    if (iconSlot) iconSlot.innerHTML = HEART_GLYPH;
    if (labelSlot) labelSlot.textContent = opts.copyLabel;
  };
  const onClick = (): void => {
    void copyText(target.address).then((ok) => {
      opts.onCopy?.(ok, target);
      if (!ok) return;
      // Морф содержимого ПРИМИТИВА: галка + «Готово» на ~1.5с, затем откат. Без своего
      // CSS/цвета/класса — только свап glyph+label (как success-морф replay-save). Цвет
      // успеха не вводим (нет токена → был бы хардкод-hex); обратная связь — тост.
      if (iconSlot) iconSlot.innerHTML = CHECK_GLYPH;
      if (labelSlot) labelSlot.textContent = opts.copiedLabel;
      if (morphTimer !== null) window.clearTimeout(morphTimer);
      morphTimer = window.setTimeout(resetMorph, 1500);
    });
  };
  btn.addEventListener('click', onClick);

  card.append(head, qr, addr, btn);
  host.appendChild(card);

  return {
    element: card,
    destroy(): void {
      if (morphTimer !== null) window.clearTimeout(morphTimer);
      btn.removeEventListener('click', onClick);
      card.remove();
    },
  };
}
